'use client'

import React, { useRef, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import type { LinkFieldType } from '@/fields/link'
import { resolveHref } from '@/utilities/resolveHref'
import { useHubSpotChat } from '@/hooks/useHubSpotChat'
import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { cn } from '@/utilities/ui'

// A title is "one line" if its rendered height is at or below this.
// Used to pick between the one-line and multi-line presets below.
const ONE_LINE_TITLE_PX = 64

// Mobile accordion stack sizing — see CardStyleStandardAccordion/index.tsx for
// how these feed into stack offset calculations.
const getCollapsedSizes = (titleH: number) =>
  titleH <= ONE_LINE_TITLE_PX
    ? { collapsedHeight: 130, stackSpacing: 90 }
    : { collapsedHeight: 180, stackSpacing: 140 }

type CardProps = {
  title: string
  description?: string
  cta?: LinkFieldType
  cardIndex: number
  isExpanded?: boolean
  isLast?: boolean
  topOffset?: number
  zIndex?: number
  onToggle?: () => void
  onHeightMeasure?: (cardIndex: number, collapsedHeight: number, stackSpacing: number, expandedHeight?: number) => void
}

export const Card: React.FC<CardProps> = ({
  title,
  description,
  cta,
  cardIndex,
  isExpanded = true,
  isLast = false,
  topOffset = 0,
  zIndex = 0,
  onToggle,
  onHeightMeasure,
}) => {
  const router = useRouter()
  const { openChat } = useHubSpotChat()
  const titleRef = useRef<HTMLHeadingElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [titleHeight, setTitleHeight] = useState(64) // Default to 2-line height
  const [expandedHeight, setExpandedHeight] = useState(220) // Default expanded card height; replaced by measurement

  // Measure title height and report back to parent. Only relevant in the mobile
  // accordion (where the parent passes onHeightMeasure); skipped on desktop.
  useEffect(() => {
    if (!titleRef.current || !onHeightMeasure) return

    const measureTitle = () => {
      const height = titleRef.current?.offsetHeight || ONE_LINE_TITLE_PX
      setTitleHeight(height)

      const { collapsedHeight, stackSpacing } = getCollapsedSizes(height)
      onHeightMeasure(cardIndex, collapsedHeight, stackSpacing)
    }

    measureTitle()

    const observer = new ResizeObserver(measureTitle)
    observer.observe(titleRef.current)

    return () => observer.disconnect()
  }, [cardIndex, onHeightMeasure])

  // Measure the expanded card's natural height for the mobile accordion stack.
  // Gated on onHeightMeasure so this only runs in the mobile accordion (where the
  // parent passes the callback) — desktop/carousel branches don't.
  // Inner content has no h-full on mobile, so its offsetHeight reflects natural
  // content height.
  useEffect(() => {
    if (!innerRef.current || !isExpanded || !onHeightMeasure) return

    const measure = () => {
      if (!innerRef.current) return
      const innerH = innerRef.current.offsetHeight
      const padding = 56 // p-7 (28px) top + bottom
      const titleH = titleRef.current?.offsetHeight || ONE_LINE_TITLE_PX
      const { collapsedHeight, stackSpacing } = getCollapsedSizes(titleH)
      // Floor expanded height at the collapsed minimum so a sparse card
      // (title-only, no description/CTA) can't end up shorter than the cards
      // stacked above it.
      const naturalH = Math.max(innerH + padding, collapsedHeight)
      setExpandedHeight(naturalH)

      onHeightMeasure(cardIndex, collapsedHeight, stackSpacing, naturalH)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(innerRef.current)
    return () => observer.disconnect()
  }, [cardIndex, isExpanded, onHeightMeasure])

  // Mobile alternates between the sub-brand's dark and light colors per card (via CSS vars from globals.css)
  const isEven = cardIndex % 2 === 0
  const mobileBgClass = isEven ? 'bg-brand-dark' : 'bg-brand-light'

  // Calculate dynamic heights based on title measurement
  const { collapsedHeight } = getCollapsedSizes(titleHeight)

  // A CTA is only actionable if it has a real destination (URL/reference or chat type).
  // Empty link fields can still be truthy objects in CMS data.
  const hasCtaAction = cta ? cta.type === 'chat' || Boolean(resolveHref(cta)) : false

  // Handle card click: expand if collapsed, navigate if expanded with CTA
  const handleCardClick = () => {
    // Collapsed: expand
    if (!isExpanded) {
      onToggle?.()
      return
    }

    // Expanded with CTA: navigate
    if (isExpanded && cta) {
      if (cta.type === 'chat') {
        openChat()
      } else {
        const href = resolveHref(cta)
        if (href) {
          if (cta.newTab) {
            window.open(href, '_blank', 'noopener,noreferrer')
          } else {
            router.push(href)
          }
        }
      }
    }
    // Expanded without CTA: do nothing
  }

  // Desktop hover: transparent → brand-dark bg, black → white text, cloud → transparent border.
  // Mobile: alternating sub-brand bg with legible text (brand-text CSS var).
  const baseClassName = cn(
    'group flex flex-col w-full p-7 md:p-8 rounded-[16px] md:rounded-[20px]',
    'absolute left-0 right-0 md:relative md:!h-auto lg:min-h-80',
    'transition-[height,top,background-color,color,border-color] duration-300 ease-in-out motion-reduce:transition-none overflow-hidden',
    mobileBgClass,
    'text-brand-text md:bg-transparent md:text-black',
    'md:hover:bg-brand-dark md:hover:text-brand-text',
    'md:border md:border-civo-cloud md:hover:border-transparent',
    (!isExpanded || hasCtaAction) && 'cursor-pointer',
    'md:shadow-none',
    !isLast && 'shadow-[1px_4px_20px_0px_rgba(0,0,0,0.3)]',
  )

  const baseStyle: React.CSSProperties = {
    top: `${topOffset}px`,
    zIndex,
  }

  // Only apply fixed height for mobile accordion mode (when isExpanded is explicitly true or false)
  if (isExpanded === true || isExpanded === false) {
    baseStyle.height = `${isExpanded ? expandedHeight : collapsedHeight}px`
  }

  return (
    <div
      className={baseClassName}
      style={baseStyle}
      onClick={handleCardClick}
    >
      <div ref={innerRef} className="flex flex-col gap-4 md:h-full md:justify-between lg:gap-12">
        {/* Title */}
        <h4 ref={titleRef} className="display-x-small md:display-small md:min-h-16">{title}</h4>

        {/*
          Bottom section (description + read-more affordance).
          On hover with a real CTA, the description translates up and the find-out-more
          fades in from absolute-bottom — so the card never grows in height. This is a
          deliberate deviation from the project's standard CSS Grid expand/collapse
          pattern (docs/animations.md): grid-template-rows would make the find-out-more
          add to the card's flow height, which we don't want here.
        */}
        {(isExpanded !== false) && (description || hasCtaAction) && (
          <div className="mt-auto relative">
            {description && (
              <p
                className={cn(
                  'body-copy-medium md:body-large md:text-black/70 md:transition-colors md:group-hover:text-brand-text',
                  hasCtaAction &&
                    'md:transition-transform md:duration-300 md:ease-in-out motion-reduce:transition-none md:group-hover:-translate-y-10',
                )}
              >
                {description}
              </p>
            )}
            {hasCtaAction && (
              <span className="subheading-small inline-flex items-center gap-1.5 mt-4 md:mt-0 md:absolute md:left-0 md:bottom-0 md:opacity-0 md:transition-opacity md:duration-300 motion-reduce:transition-none md:group-hover:opacity-100">
                <span className="underline underline-offset-4">{cta?.label || 'Find out more'}</span>
                <span aria-hidden="true" className="inline-flex transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1">
                  <ArrowIcon className="size-4" />
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
